import React, { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { formatNumber, formatWeaponName, getWeaponIconUrl } from '../../utils/formatters';
import MapImage from '../common/MapImage';
import CircularProgress from '../common/CircularProgress';
import BodyHitmap from '../stats/BodyHitmap';
import PlayerHeatmap from '../stats/PlayerHeatmap';
import HighlightCard from '../highlights/HighlightCard';

// Emoji per inferred playstyle role (Fas 4).
const ROLE_ICONS = {
  'AWPer': '🎯',
  'Entry Fragger': '⚡',
  'Support': '💨',
  'Lurker': '🥷',
  'Rifler': '🔫',
};

/**
 * Playstyle role badge + signal fingerprint (Fas 4).
 *
 * Rendered on the profile's overview tab. Hidden while the role is still
 * "Unranked" (not enough analyzed demos to infer a role).
 *
 * @param {Object} props
 * @param {Object|null} props.advancedStats - Player advanced stats payload.
 * @returns {JSX.Element|null} The section, or null without a ranked role.
 */
export function PlaystyleSection({ advancedStats }) {
  const { t } = useTranslation();
  if (!advancedStats?.role || advancedStats.role.primary === 'Unranked') return null;

  return (
    <div className="stats-section">
      <h3>🎭 {t('playerStats.secPlaystyle')}</h3>
      <div className="playstyle-block">
        <div className="playstyle-badge">
          <span className="playstyle-role-icon">{ROLE_ICONS[advancedStats.role.primary] || '🎮'}</span>
          <span className="playstyle-role-name">{advancedStats.role.primary}</span>
        </div>
        <div className="stats-grid playstyle-signals">
          <div className="stat-item">
            <span className="label">{t('playerStats.openingShare')}</span>
            <span className="value">{advancedStats.role.signals.opening_share}%</span>
          </div>
          <div className="stat-item">
            <span className="label">{t('playerStats.awpShare')}</span>
            <span className="value">{advancedStats.role.signals.awp_share}%</span>
          </div>
          <div className="stat-item">
            <span className="label">{t('playerStats.tradeShare')}</span>
            <span className="value">{advancedStats.role.signals.trade_share}%</span>
          </div>
          <div className="stat-item">
            <span className="label">UD / Round</span>
            <span className="value">{advancedStats.role.signals.ud_per_round}</span>
          </div>
        </div>
      </div>
    </div>
  );
}

/**
 * The "Statistik" profile tab: performance totals, clutches, entries,
 * opening duels, aim mechanics, utility, economy, round context, movement
 * and multi-kills.
 *
 * @param {Object} props
 * @param {Object} props.player - Base player payload (/api/player/{id}).
 * @param {Object|null} props.advancedStats - Advanced stats payload, or null.
 * @returns {JSX.Element} The tab content.
 */
export function CoreStatsTab({ player, advancedStats }) {
  const { t } = useTranslation();

  return (
    <>
      <div className="stats-section">
        <h3>📊 {t('playerStats.secPerformance')}</h3>
        <div className="stats-grid">
          <div className="stat-item">
            <span className="label">{t('playerStats.totalKills')}</span>
            <span className="value">{formatNumber(player.overview.total_kills)}</span>
          </div>
          <div className="stat-item">
            <span className="label">{t('playerStats.totalDeaths')}</span>
            <span className="value">{formatNumber(player.overview.total_deaths)}</span>
          </div>
          <div className="stat-item">
            <span className="label">{t('playerStats.totalAssists')}</span>
            <span className="value">{formatNumber(player.overview.total_assists)}</span>
          </div>
          <div className="stat-item">
            <span className="label">{t('playerStats.accuracy')}</span>
            <span className="value">{player.overview.accuracy}%</span>
          </div>
        </div>
      </div>

      <div className="stats-section">
        <h3>🏆 {t('playerStats.secClutches')}</h3>
        <div className="stats-grid">
          <div className="stat-item">
            <span className="label">{t('playerStats.clutch1v1Won')}</span>
            <span className="value">{player.clutch_stats.v1_wins}/{player.clutch_stats.v1_attempts}</span>
          </div>
          <div className="stat-item">
            <span className="label">{t('playerStats.clutch1v2Won')}</span>
            <span className="value">{player.clutch_stats.v2_wins}/{player.clutch_stats.v2_attempts}</span>
          </div>
          <div className="stat-item highlight">
            <span className="label">{t('playerStats.totalClutches')}</span>
            <span className="value">{player.clutch_stats.total_clutches}</span>
          </div>
        </div>
      </div>

      <div className="stats-section">
        <h3>⚡ {t('playerStats.secEntryFrags')}</h3>
        <div className="stats-grid">
          <div className="stat-item">
            <span className="label">{t('playerStats.entryKills')}</span>
            <span className="value">{player.entry_stats.entry_wins}</span>
          </div>
          <div className="stat-item">
            <span className="label">{t('playerStats.attempts')}</span>
            <span className="value">{player.entry_stats.entry_attempts}</span>
          </div>
          <div className="stat-item">
            <span className="label">{t('playerStats.successRate')}</span>
            <span className="value">{player.entry_stats.success_rate}%</span>
          </div>
        </div>
      </div>

      {advancedStats && !advancedStats.error && (
        <div className="stats-section">
          <h3>{t('playerStats.secOpeningDuels')}</h3>
          <div className="stats-grid">
            <div className="stat-item">
              <span className="label">{t('playerStats.openingDuelWinPct')}</span>
              <span className="value">{advancedStats.opening_duel_win_pct}%</span>
            </div>
            <div className="stat-item">
              <span className="label">{t('playerStats.openingKills')}</span>
              <span className="value">{advancedStats.opening_kills}</span>
            </div>
            <div className="stat-item">
              <span className="label">{t('playerStats.openingDeaths')}</span>
              <span className="value">{advancedStats.opening_deaths}</span>
            </div>
            <div className="stat-item">
              <span className="label">{t('playerStats.tradeKills')}</span>
              <span className="value">{advancedStats.trade_kills}</span>
            </div>
            <div className="stat-item">
              <span className="label">{t('playerStats.hsKills')}</span>
              <span className="value">{advancedStats.headshot_kills}</span>
            </div>
            <div className="stat-item">
              <span className="label">HS%</span>
              <span className="value">{advancedStats.headshot_pct}%</span>
            </div>
          </div>
        </div>
      )}

      {advancedStats?.mechanics && (
        <div className="stats-section">
          <h3>🎯 {t('playerStats.secAimMechanics')}</h3>
          <div className="stats-grid">
            <div className="stat-item">
              <span className="label">{t('playerStats.accuracy')}</span>
              <span className="value">{advancedStats.mechanics.accuracy}%</span>
            </div>
            <div className="stat-item">
              <span className="label">{t('playerStats.rifleAccuracy')}</span>
              <span className="value">{advancedStats.mechanics.rifle_accuracy}%</span>
            </div>
            <div className="stat-item highlight">
              <span className="label">{t('playerStats.headHitPct')}</span>
              <span className="value">{advancedStats.mechanics.head_hit_pct}%</span>
            </div>
          </div>
        </div>
      )}

      {advancedStats?.utility && (
        <div className="stats-section">
          <h3>💨 {t('playerStats.secUtilitySupport')}</h3>
          <div className="stats-grid">
            <div className="stat-item highlight">
              <span className="label">{t('playerStats.utilityDamage')}</span>
              <span className="value">{formatNumber(advancedStats.utility.utility_damage)}</span>
            </div>
            <div className="stat-item">
              <span className="label">UD / Round</span>
              <span className="value">{advancedStats.utility.ud_per_round}</span>
            </div>
            <div className="stat-item">
              <span className="label">{t('playerStats.flashAssists')}</span>
              <span className="value">{advancedStats.utility.flash_assists}</span>
            </div>
            <div className="stat-item">
              <span className="label">{t('playerStats.enemiesFlashed')}</span>
              <span className="value">{advancedStats.utility.enemies_flashed}</span>
            </div>
            <div className="stat-item">
              <span className="label">{t('playerStats.enemyBlindTime')}</span>
              <span className="value">{advancedStats.utility.enemy_flash_duration}s</span>
            </div>
            <div className="stat-item">
              <span className="label">{t('playerStats.flashSuccess')}</span>
              <span className="value">{player.utility_stats.flash_successes}/{player.utility_stats.flash_attempts}</span>
            </div>
            <div className="stat-item">
              <span className="label">Nades / Round</span>
              <span className="value">{advancedStats.utility.nades_per_round}</span>
            </div>
          </div>
        </div>
      )}

      {advancedStats?.economy && advancedStats.economy.avg_equipment_value > 0 && (
        <div className="stats-section">
          <h3>💰 {t('playerStats.secEconomy')}</h3>
          <div className="stats-grid">
            <div className="stat-item highlight">
              <span className="label">{t('playerStats.avgEquipValue')}</span>
              <span className="value">${formatNumber(advancedStats.economy.avg_equipment_value)}</span>
            </div>
            <div className="stat-item">
              <span className="label">{t('playerStats.pistolWinPct')}</span>
              <span className="value">{advancedStats.economy.pistol_win_rate}%</span>
            </div>
            <div className="stat-item">
              <span className="label">{t('playerStats.ecoWinPct')}</span>
              <span className="value">{advancedStats.economy.eco_win_rate}%</span>
            </div>
            <div className="stat-item">
              <span className="label">{t('playerStats.forceWinPct')}</span>
              <span className="value">{advancedStats.economy.force_win_rate}%</span>
            </div>
            <div className="stat-item">
              <span className="label">{t('playerStats.fullBuyWinPct')}</span>
              <span className="value">{advancedStats.economy.full_buy_win_rate}%</span>
            </div>
          </div>
        </div>
      )}

      {advancedStats?.round_context && (
        <div className="stats-section">
          <h3>🧭 {t('playerStats.secRoundContext')}</h3>
          <div className="stats-grid">
            <div className="stat-item">
              <span className="label">{t('playerStats.postPlantWinPct')}</span>
              <span className="value">
                {advancedStats.round_context.post_plant_win_rate}%
                <span className="stat-subnote"> ({advancedStats.round_context.post_plant_rounds})</span>
              </span>
            </div>
            <div className="stat-item">
              <span className="label">{t('playerStats.retakeWinPct')}</span>
              <span className="value">
                {advancedStats.round_context.retake_win_rate}%
                <span className="stat-subnote"> ({advancedStats.round_context.retake_rounds})</span>
              </span>
            </div>
            <div className="stat-item highlight">
              <span className="label">{t('playerStats.manAdvConversion')}</span>
              <span className="value">
                {advancedStats.round_context.man_adv_conversion}%
                <span className="stat-subnote"> ({advancedStats.round_context.man_adv_rounds})</span>
              </span>
            </div>
          </div>
        </div>
      )}

      {advancedStats?.movement && advancedStats.movement.distance_m > 0 && (
        <div className="stats-section">
          <h3>🏃 {t('playerStats.movement')}</h3>
          <div className="stats-grid">
            <div className="stat-item">
              <span className="label">{t('playerStats.distanceTravelled')}</span>
              <span className="value">{formatNumber(Math.round(advancedStats.movement.distance_m))} m</span>
            </div>
            <div className="stat-item">
              <span className="label">{t('playerStats.perMatch')}</span>
              <span className="value">{formatNumber(Math.round(advancedStats.movement.distance_per_match_m))} m</span>
            </div>
          </div>
        </div>
      )}

      <div className="stats-section">
        <h3>{t('playerStats.secMultiKills')}</h3>
        <div className="multi-kills">
          <div className="multi-kill-item ace">
            <span className="kill-count">5K</span>
            <span className="kill-value">{player.multi_kills['5k']}</span>
          </div>
          <div className="multi-kill-item">
            <span className="kill-count">4K</span>
            <span className="kill-value">{player.multi_kills['4k']}</span>
          </div>
          <div className="multi-kill-item">
            <span className="kill-count">3K</span>
            <span className="kill-value">{player.multi_kills['3k']}</span>
          </div>
          <div className="multi-kill-item">
            <span className="kill-count">2K</span>
            <span className="kill-value">{player.multi_kills['2k']}</span>
          </div>
        </div>
      </div>

      {/* Legacy MatchZy utility — only as a fallback when the richer demo-based
          "Utility & Support" block above is absent, so we never show both. */}
      {!advancedStats?.utility && (
        <div className="stats-section">
          <h3>💨 {t('medals.catUtility')}</h3>
          <div className="stats-grid">
            <div className="stat-item">
              <span className="label">{t('playerStats.enemiesFlashed')}</span>
              <span className="value">{player.utility_stats.enemies_flashed}</span>
            </div>
            <div className="stat-item">
              <span className="label">{t('playerStats.flashSuccess')}</span>
              <span className="value">{player.utility_stats.flash_successes}/{player.utility_stats.flash_attempts}</span>
            </div>
            <div className="stat-item">
              <span className="label">{t('playerStats.utilityDamage')}</span>
              <span className="value">{player.utility_stats.utility_damage}</span>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

/**
 * The "Vapen & kartor" profile tab: weapon table with expandable hitgroup
 * bars + body hitmap filter, per-map performance cards (incl. map ELO) and
 * the position heatmap.
 *
 * @param {Object} props
 * @param {Object} props.player - Base player payload (map_stats).
 * @param {string} props.steamid64 - The player's steamid64 (heatmap fetch).
 * @param {Object|null} props.advancedStats - Advanced stats (map win rates/ELO).
 * @param {Object|null} props.weaponStats - Weapon stats payload, or null.
 * @returns {JSX.Element} The tab content.
 */
export function WeaponsMapsTab({ player, steamid64, advancedStats, weaponStats }) {
  const { t } = useTranslation();
  const [expandedWeapon, setExpandedWeapon] = useState(null);
  const [hitmapWeapon, setHitmapWeapon] = useState(null);

  const hitmapData = useMemo(() => {
    if (!weaponStats?.weapons?.length) return null;
    if (hitmapWeapon) {
      const w = weaponStats.weapons.find(x => x.weapon === hitmapWeapon);
      return w?.hitgroups || null;
    }
    return weaponStats.weapons.reduce((acc, w) => {
      if (!w.hitgroups) return acc;
      return {
        head: acc.head + w.hitgroups.head,
        chest: acc.chest + w.hitgroups.chest,
        stomach: acc.stomach + w.hitgroups.stomach,
        arm: acc.arm + w.hitgroups.arm,
        leg: acc.leg + w.hitgroups.leg,
      };
    }, { head: 0, chest: 0, stomach: 0, arm: 0, leg: 0 });
  }, [weaponStats, hitmapWeapon]);

  return (
    <>
      {weaponStats?.weapons?.length > 0 && (
        <>
          <div className="stats-section">
            <div className="weapon-section-header">
              <h3>{t('playerStats.secWeaponStats')}</h3>
              <BodyHitmap
                hitgroups={hitmapData}
                weaponFilter={hitmapWeapon ? formatWeaponName(hitmapWeapon) : null}
              />
              {hitmapWeapon && (
                <button
                  className="hitmap-filter-reset"
                  onClick={() => setHitmapWeapon(null)}
                >
                  {t('playerStats.showAllWeapons')}
                </button>
              )}
            </div>
          </div>
          <div className="stats-section">
            <div className="weapon-stats-table">
              <table>
                <thead>
                  <tr>
                    <th className="weapon-col-name">{t('playerStats.colWeapon')}</th>
                    <th>K/D</th>
                    <th>{t('playerStats.kills')}</th>
                    <th className="weapon-col-hs">HS%</th>
                    <th className="weapon-col-acc">{t('playerStats.accuracy')}</th>
                    <th className="weapon-col-hide-mobile">{t('playerStats.colDamage')}</th>
                  </tr>
                </thead>
                <tbody>
                  {weaponStats.weapons.slice(0, 15).map(w => {
                    const isExpanded = expandedWeapon === w.weapon;
                    const totalHits = w.hitgroups
                      ? w.hitgroups.head + w.hitgroups.chest + w.hitgroups.stomach + w.hitgroups.arm + w.hitgroups.leg
                      : 0;
                    const kd = w.deaths > 0 ? (w.kills / w.deaths).toFixed(2) : w.kills.toFixed(2);
                    const hsColor = w.hs_pct >= 50 ? '#22c55e' : w.hs_pct >= 30 ? '#eab308' : '#ff6b00';
                    const accColor = w.accuracy >= 30 ? '#22c55e' : w.accuracy >= 15 ? '#eab308' : '#ff6b00';
                    return (
                      <React.Fragment key={w.weapon}>
                        <tr
                          className={`weapon-row ${isExpanded ? 'expanded' : ''} ${totalHits > 0 ? 'expandable' : ''}`}
                          onClick={() => {
                            if (totalHits > 0) setExpandedWeapon(isExpanded ? null : w.weapon);
                            setHitmapWeapon(hitmapWeapon === w.weapon ? null : w.weapon);
                          }}
                        >
                          <td className="weapon-col-name">
                            <div className="weapon-name-cell">
                              {getWeaponIconUrl(w.weapon) && (
                                <img
                                  src={getWeaponIconUrl(w.weapon)}
                                  alt=""
                                  className="weapon-icon"
                                  onError={(e) => { e.target.style.display = 'none'; }}
                                />
                              )}
                              {formatWeaponName(w.weapon)}
                            </div>
                          </td>
                          <td className="weapon-kd">{kd}</td>
                          <td className="weapon-kills">{w.kills}</td>
                          <td className="weapon-col-hs">
                            <span className="weapon-pct-cell">
                              <CircularProgress value={w.hs_pct} color={hsColor} />
                              <span>{w.hs_pct}%</span>
                            </span>
                          </td>
                          <td className="weapon-col-acc">
                            <span className="weapon-pct-cell">
                              <CircularProgress value={w.accuracy} color={accColor} />
                              <span>{w.accuracy}%</span>
                            </span>
                          </td>
                          <td className="weapon-col-hide-mobile">{formatNumber(w.damage)}</td>
                        </tr>
                        {isExpanded && totalHits > 0 && (
                          <tr className="weapon-hitgroup-row">
                            <td colSpan="6">
                              <div className="hitgroup-bars">
                                {[
                                  { label: t('playerStats.hgHead'), value: w.hitgroups.head, cls: 'head' },
                                  { label: t('playerStats.hgChest'), value: w.hitgroups.chest, cls: 'chest' },
                                  { label: t('playerStats.hgStomach'), value: w.hitgroups.stomach, cls: 'stomach' },
                                  { label: t('playerStats.hgArms'), value: w.hitgroups.arm, cls: 'arm' },
                                  { label: t('playerStats.hgLegs'), value: w.hitgroups.leg, cls: 'leg' },
                                ].map(hg => (
                                  <div key={hg.cls} className="hitgroup-item">
                                    <span className="hitgroup-label">{hg.label}</span>
                                    <div className="hitgroup-bar-bg">
                                      <div
                                        className={`hitgroup-bar-fill ${hg.cls}`}
                                        style={{ width: `${(hg.value / totalHits) * 100}%` }}
                                      />
                                    </div>
                                    <span className="hitgroup-pct">
                                      {Math.round((hg.value / totalHits) * 100)}%
                                    </span>
                                  </div>
                                ))}
                              </div>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}

      {player.map_stats && player.map_stats.length > 0 && (
        <div className="stats-section">
          <h3>{t('playerStats.secMapPerformance')}</h3>
          <div className="map-stats-grid">
            {player.map_stats.map(map => (
              <div key={map.mapname} className="map-stat-card">
                <div className="map-stat-image">
                  <MapImage imageUrl={map.image_url} mapName={map.mapname} />
                  <div className="map-stat-image-overlay">
                    <span className="map-stat-name">{map.mapname}</span>
                  </div>
                </div>
                <div className="map-stat-details">
                  <div className="map-stat-row-item">
                    <span className="map-stat-label">{t('nav.matches')}</span>
                    <span className="map-stat-value">{map.matches_played}</span>
                  </div>
                  <div className="map-stat-row-item">
                    <span className="map-stat-label">K/D</span>
                    <span className="map-stat-value highlight-orange">{map.kd_ratio}</span>
                  </div>
                  <div className="map-stat-row-item">
                    <span className="map-stat-label">ADR</span>
                    <span className="map-stat-value highlight-blue">{map.avg_adr}</span>
                  </div>
                  {map.hs_pct != null && (
                    <div className="map-stat-row-item">
                      <span className="map-stat-label">HS%</span>
                      <span className="map-stat-value">{map.hs_pct}%</span>
                    </div>
                  )}
                  {advancedStats?.map_win_rates?.[map.mapname] && (
                    <div className="map-stat-row-item">
                      <span className="map-stat-label">{t('playerStats.winPct')}</span>
                      <span className="map-stat-value highlight-green">
                        {advancedStats.map_win_rates[map.mapname].win_rate}%
                      </span>
                    </div>
                  )}
                  {advancedStats?.map_elos?.[map.mapname] && (
                    <div className="map-stat-row-item">
                      <span className="map-stat-label">{t('playerStats.mapElo')}</span>
                      <span className={`map-stat-value ${advancedStats.map_elos[map.mapname].elo >= 1000 ? 'highlight-green' : 'highlight-orange'}`}>
                        {advancedStats.map_elos[map.mapname].elo >= 1000 ? '+' : ''}
                        {advancedStats.map_elos[map.mapname].elo - 1000}
                      </span>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <PlayerHeatmap steamid64={steamid64} />
    </>
  );
}

/**
 * Highlight clips section for the "Prestationer" profile tab.
 *
 * @param {Object} props
 * @param {Array} props.highlights - Highlight clips for the player.
 * @param {Function} [props.onSelectMatch] - Navigate to a match page.
 * @returns {JSX.Element|null} The section, or null without highlights.
 */
export function HighlightsSection({ highlights, onSelectMatch }) {
  const { t } = useTranslation();
  if (!highlights || highlights.length === 0) return null;

  return (
    <div className="stats-section">
      <h3>🎬 {t('nav.highlights')}</h3>
      <div className="highlights-grid">
        {highlights.map(h => (
          <HighlightCard
            key={h.id}
            highlight={h}
            onClick={(matchId) => onSelectMatch && onSelectMatch(matchId)}
          />
        ))}
      </div>
    </div>
  );
}
